import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../auth/AuthProvider';
import { LangToggle, useT } from '../i18n';

const NAV_ITEMS = [
  { to: '/dashboard', labelKey: 'nav.passes' },
  { to: '/studio/apple', labelKey: 'nav.appleStudio' },
  { to: '/studio/google', labelKey: 'nav.googleStudio' },
] as const;

export function Layout(): JSX.Element {
  const { partner, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const t = useT();
  // Phone widths: the inline nav + language toggle + e-mail + sign-out
  // overflowed the header (the sign-out was pushed off-screen), so below `md`
  // the nav lives behind a menu button. Rendered only while open, so the
  // desktop nav stays the single set of links in the DOM otherwise.
  const [menuOpen, setMenuOpen] = useState(false);

  // Any navigation (a link, the browser back button) closes the menu.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleSignOut = (): void => {
    signOut();
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2 min-w-0">
            <span className="h-7 w-7 flex-shrink-0 rounded-md bg-brand text-white grid place-items-center font-bold text-sm">
              i
            </span>
            <span className="font-semibold text-slate-900 truncate">
              Insurwiz<span className="hidden sm:inline"> Wallet Demo</span>
            </span>
            <span className="text-xs uppercase tracking-wider rounded bg-amber-100 text-amber-800 px-1.5 py-0.5 font-bold">
              Beta
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-5 text-sm">
            {NAV_ITEMS.map((item) => (
              <NavLink key={item.to} to={item.to} className={navClass}>
                {t(item.labelKey)}
              </NavLink>
            ))}
            <LangToggle />
            {partner ? (
              <>
                <span className="text-slate-500 hidden lg:block">{partner.email}</span>
                <button onClick={handleSignOut} className="btn-secondary py-1 text-xs">
                  {t('nav.signOut')}
                </button>
              </>
            ) : null}
          </nav>

          <button
            type="button"
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
            aria-label={t('nav.menu')}
            onClick={() => setMenuOpen((open) => !open)}
            className="md:hidden inline-flex h-9 w-9 items-center justify-center rounded-md ring-1 ring-slate-200 text-slate-700 hover:bg-slate-50"
          >
            <span aria-hidden="true" className="text-lg leading-none">
              {menuOpen ? '✕' : '☰'}
            </span>
          </button>
        </div>

        {menuOpen ? (
          <nav
            id="mobile-menu"
            aria-label={t('nav.mobileMenu')}
            className="md:hidden border-t border-slate-200 px-4 py-3 space-y-1"
          >
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `block rounded-md px-3 py-2 text-sm ${
                    isActive ? 'bg-brand-50 text-brand-700 font-medium' : 'text-slate-700'
                  }`
                }
              >
                {t(item.labelKey)}
              </NavLink>
            ))}
            <div className="flex items-center justify-between gap-3 pt-3 mt-2 border-t border-slate-100">
              <LangToggle />
              {partner ? (
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-xs text-slate-500 truncate">{partner.email}</span>
                  <button onClick={handleSignOut} className="btn-secondary py-1 text-xs">
                    {t('nav.signOut')}
                  </button>
                </div>
              ) : null}
            </div>
          </nav>
        ) : null}
      </header>
      <main className="flex-1">
        <Outlet />
      </main>
      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 text-xs text-slate-500 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <span>{t('footer.copyright')}</span>
          <span className="flex items-center gap-4">
            <Link to="/terms" className="text-brand-700 hover:underline">
              {t('footer.terms')}
            </Link>
            <a href="mailto:hello@insurwiz.io" className="text-brand-700 hover:underline">
              hello@insurwiz.io
            </a>
          </span>
        </div>
      </footer>
    </div>
  );
}

function navClass({ isActive }: { isActive: boolean }): string {
  return isActive ? 'text-brand-700 font-medium' : 'text-slate-600 hover:text-slate-900';
}
